import {useState, useEffect, useCallback} from "react";
import {useAuth} from "../../sdk";


const sortMultipliersByMaxRooms = (data) => {
  const sortedData = {};

  // Iterate over 'current_day', 'next_day', 'day_after_tomorrow'
  for (const dayKey in data) {
    sortedData[dayKey] = {};

    // Each day has 'weekday' and 'weekend'
    for (const typeKey in data[dayKey]) {
      sortedData[dayKey][typeKey] = {};

      // Each has multiple time keys like "15:00:00", "16:00:00"
      for (const timeKey in data[dayKey][typeKey]) {
        const timeArray = data[dayKey][typeKey][timeKey];

        // Sort descending by maximumRooms
        sortedData[dayKey][typeKey][timeKey] = [...timeArray].sort(
          (a, b) => b.maximumRooms - a.maximumRooms
        );
      }
    }
  }

  return sortedData;
};


export function useLastDay(hotelId, copyFromHotelId, LD) {
  const [lastDayData, setLastDayData] = useState({});
  const [isCancelled, setIsCancelled] = useState(false);
  const [editMode, setEditMode] = useState({
    current_day: false,
    next_day: false,
    day_after_tomorrow: false,
  });
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [networkMsg, setNetworkMsg] = useState("");
  const {authFetch} = useAuth();

  const handleCloseStatus = useCallback(() => {
    setOpen(null);
  }, []);

  const refreshLastDayData = useCallback(
    async (dayKey, targetHotelId = hotelId) => {
      if (!targetHotelId) return;

      if (dayKey) {
        setLoading(dayKey);
      } else {
        setLoading("all");
      }

      try {
        const {get} = await authFetch({
          path: `/hotel/${targetHotelId}/last-day-multiplier`,
        });
        const {data, error, response} = await get();

        if (data && Object.keys(data).length > 0 && response?.ok) {
          const sortedData = sortMultipliersByMaxRooms(data)
          setLastDayData(sortedData);
        } else if (error) {
          console.error(error);
          setLastDayData({});
          setNetworkMsg(error.message || "Failed to fetch last day data");
          setOpen("error");
        } else {
          setLastDayData({});
        }
      } catch (error) {
        console.error("Error fetching last day data:", error);
        setNetworkMsg("Failed to fetch data");
        setOpen("error");
      } finally {
        setLoading(null);
      }
    },
    [hotelId, authFetch]
  );

  const updateLastDayData = useCallback(
    async (payload, dayKey) => {
      if (!payload) return;
      if (!hotelId || payload.length === 0) return;

      if (dayKey) {
        setLoading(dayKey);
      } else {
        setLoading("all");
      }
      try {
        const {post} = await authFetch({
          path: `/hotel/${hotelId}/last-day-multiplier`,
        });

        const updatedPayload = {
          isCopy: LD,
          applicableDay: dayKey,
          multiplier: payload,
        };

        const {data, error, response} = await post(updatedPayload);

        if (data && response?.ok) {
          if(!LD) refreshLastDayData(dayKey)
          setNetworkMsg("Last day multiplier updated successfully");
          setOpen("success");
          return true;
        } else {
          setNetworkMsg(
            data?.messageToUser || "Error updating last day multiplier"
          );
          setOpen("error");
          console.log(error);
          return false;
        }
      } catch (error) {
        setNetworkMsg("Network error");
        setOpen("error");
        console.error("Error updating last day data:", error);
        return false;
      } finally {
        setLoading(null);
      }
    },
    [hotelId, authFetch, LD]
  );

  const deleteTimeSlotRow = useCallback(
    async (timeSlot, dayKey, isWeekDay) => {
      if (!hotelId || !timeSlot || !dayKey || isWeekDay === null) return;

      try {
        const {delete: deleteFn} = await authFetch({
          path: `/hotel/${hotelId}/last-day-multiplier/${dayKey}/${timeSlot}/${isWeekDay}`,
        });

        const {data, error, response} = await deleteFn();

        if (data && response?.ok) {
          refreshLastDayData(dayKey);
          setNetworkMsg("Last day multiplier updated successfully");
          setOpen("success");
          // setIsInEditMode(false);
        } else {
          setNetworkMsg(
            data?.messageToUser || "Error updating last day multiplier"
          );
          setOpen("error");
          console.log(error);
          return false;
        }
      } catch (error) {
        setNetworkMsg("Network error");
        setOpen("error");
        console.error("Error updating last day data:", error);
        return false;
      }
    },
    [authFetch, hotelId, refreshLastDayData]
  );

  const deleteRowInTimeSlot = useCallback(
    async (rowId, dayKey) => {
      if (!hotelId || !rowId || !dayKey) return;

      try {
        const {delete: deleteFunc} = await authFetch({
          path: `/hotel/${hotelId}/last-day-multiplier/${rowId}`,
        });

        const {data, error, response} = await deleteFunc();

        if (data && response?.ok) {
          refreshLastDayData(dayKey);
          setNetworkMsg("Last day multiplier updated successfully");
          setOpen("success");
        } else {
          setNetworkMsg(
            data?.messageToUser || "Error updating last day multiplier"
          );
          setOpen("error");
          console.log(error);
          return false;
        }
      } catch (error) {
        setNetworkMsg("Network error");
        setOpen("error");
        console.error("Error updating last day data:", error);
        return false;
      }
    },
    [authFetch, hotelId, refreshLastDayData]
  );

  // Initial data fetch
  useEffect(() => {
    if (hotelId) {
      refreshLastDayData();
    }
  }, [hotelId, refreshLastDayData]);

  // Copy data from another hotel
  useEffect(() => {
    if (copyFromHotelId && LD) {
      refreshLastDayData("all",copyFromHotelId);
      setEditMode({
        current_day: true,
        next_day: true,
        day_after_tomorrow: true,
      });
    }
  }, [copyFromHotelId, LD, refreshLastDayData]);

  // Handle save/cancel actions
  useEffect(() => {
    if (isSaved) {
      setIsSaved(false);
    } else if (isCancelled) {
      refreshLastDayData(isCancelled);
      setIsCancelled(null);
    }
  }, [isSaved, isCancelled, updateLastDayData, refreshLastDayData]);

  return {
    lastDayData,
    ldSetIsCancelled: setIsCancelled,
    ldSetIsSaved: setIsSaved,
    ldOpen: open,
    ldHandleCloseStatus: handleCloseStatus,
    ldNetworkMsg: networkMsg,
    ldLoading: loading,
    updateLastDayData,
    deleteTimeSlotRow,
    deleteRowInTimeSlot,
    ldEditMode: editMode,
    ldSetEditMode: setEditMode
  };
}
